import React, { useEffect, useMemo, useState } from 'react';
import config from '../config';
import { detailToString } from '../utils/apiError';

// Palette matching packaging_guide.py _COLOR_PALETTE so the UI preview matches the PDF.
const COLOR_PALETTE = [
  'rgb(209, 227, 255)', // blue
  'rgb(219, 247, 209)', // green
  'rgb(255, 222, 179)', // orange
  'rgb(240, 204, 255)', // purple
  'rgb(255, 247, 191)', // yellow
  'rgb(199, 250, 242)', // cyan
  'rgb(255, 204, 217)', // pink
  'rgb(219, 219, 235)', // slate
];

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function PackagingGuidePage() {
  // Mode: "jobs" (default) or "sales_orders"
  const [mode, setMode] = useState('jobs');

  // Data
  const [jobs, setJobs] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadError, setLoadError] = useState('');

  // Filters
  const [customerFilter, setCustomerFilter] = useState('');
  const [search, setSearch] = useState('');
  const [maxWeight, setMaxWeight] = useState('');

  // Selection (independent per mode so switching modes doesn't lose picks)
  const [selectedJobIds, setSelectedJobIds] = useState(new Set());
  const [selectedSoIds, setSelectedSoIds] = useState(new Set());
  const selectedIds = mode === 'jobs' ? selectedJobIds : selectedSoIds;
  const setSelectedIds = mode === 'jobs' ? setSelectedJobIds : setSelectedSoIds;

  // Plan result
  const [planning, setPlanning] = useState(false);
  const [planError, setPlanError] = useState('');
  const [plan, setPlan] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Load the active list (jobs or SOs) when mode or customer filter changes
  useEffect(() => {
    async function loadList() {
      setLoadingList(true);
      setLoadError('');
      try {
        const params = new URLSearchParams();
        if (customerFilter) params.set('customer_id', customerFilter);
        const path =
          mode === 'jobs' ? '/packaging/eligible-jobs' : '/packaging/eligible-sales-orders';
        const res = await fetch(`${config.API_BASE_URL}${path}?${params.toString()}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (mode === 'jobs') setJobs(data.jobs || []);
        else setSalesOrders(data.sales_orders || []);
      } catch (err) {
        setLoadError(`Error loading ${mode === 'jobs' ? 'jobs' : 'sales orders'}: ${err.message}`);
      } finally {
        setLoadingList(false);
      }
    }
    loadList();
  }, [mode, customerFilter]);

  // Load customers list once for the filter dropdown (excluding Bad Idea id=1)
  useEffect(() => {
    async function loadCustomers() {
      try {
        const res = await fetch(`${config.API_BASE_URL}/customers`);
        if (!res.ok) return;
        const data = await res.json();
        const filtered = (Array.isArray(data) ? data : data?.customers || [])
          .filter((c) => c.customer_id !== 1)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setCustomers(filtered);
      } catch (_err) {
        // Non-fatal
      }
    }
    loadCustomers();
  }, []);

  // Client-side search filter
  const filteredJobs = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return jobs;
    return jobs.filter((j) => {
      const parts = [
        String(j.master_job_num || ''),
        j.job_description || '',
        j.customer_po || '',
        j.customer_so || '',
        j.customer_name || '',
      ];
      return parts.some((p) => p.toLowerCase().includes(q));
    });
  }, [jobs, search]);

  const filteredSalesOrders = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return salesOrders;
    return salesOrders.filter((s) => {
      const parts = [
        s.order_number || '',
        s.customer_po || '',
        s.customer_so || '',
        s.customer_name || '',
      ];
      return parts.some((p) => p.toLowerCase().includes(q));
    });
  }, [salesOrders, search]);

  const filteredList = mode === 'jobs' ? filteredJobs : filteredSalesOrders;

  function toggleId(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAllFiltered() {
    if (mode === 'jobs') {
      setSelectedJobIds(new Set(filteredJobs.map((j) => j.job_id)));
    } else {
      setSelectedSoIds(new Set(filteredSalesOrders.map((s) => s.sales_order_id)));
    }
  }

  function clearSelection() {
    setSelectedIds(new Set());
    setPlan(null);
    setPlanError('');
  }

  function switchMode(nextMode) {
    if (nextMode === mode) return;
    setMode(nextMode);
    setPlan(null);
    setPlanError('');
    setSearch('');
  }

  function planBody() {
    if (mode === 'jobs') {
      return {
        job_ids: Array.from(selectedJobIds),
        max_weight_lbs: maxWeight === '' ? null : Number(maxWeight),
      };
    }
    return {
      sales_order_ids: Array.from(selectedSoIds),
      max_weight_lbs: maxWeight === '' ? null : Number(maxWeight),
    };
  }

  async function handleGeneratePlan() {
    if (selectedIds.size === 0) {
      setPlanError(mode === 'jobs' ? 'Select at least one job.' : 'Select at least one sales order.');
      return;
    }
    setPlanning(true);
    setPlanError('');
    setPlan(null);
    try {
      const res = await fetch(`${config.API_BASE_URL}/packaging/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(planBody()),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(detailToString(errBody.detail) || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setPlan(data);
    } catch (err) {
      setPlanError(`Error: ${err.message}`);
    } finally {
      setPlanning(false);
    }
  }

  async function handleDownloadPdf() {
    if (selectedIds.size === 0) return;
    setDownloadingPdf(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/packaging/plan/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(planBody()),
      });
      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        throw new Error(errText || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const soNum = plan?.sales_orders?.[0]?.order_number;
      const jobNum = plan?.jobs?.[0]?.master_job_num;
      a.download = soNum
        ? `Packaging_Guide_${soNum}.pdf`
        : jobNum
        ? `Packaging_Guide_Job${jobNum}.pdf`
        : 'Packaging_Guide.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      setPlanError(`PDF download failed: ${err.message}`);
    } finally {
      setDownloadingPdf(false);
    }
  }

  return (
    <div className="container-fluid my-4" style={{ maxWidth: 1400 }}>
      <h2><i className="fas fa-box me-2"></i>Packaging Guide</h2>
      <p className="text-muted mb-3">
        Pack flat laser-cut parts into Uline corrugated boxes. Pick jobs to plan
        individual runs, or pick a sales order to plan the whole pallet. The
        planner keeps each job's parts in its own boxes and picks one uniform
        box size per job (minimum boxes, preferring clean 40×48 pallet fit).
        Parts with brake/weld steps and over-sized parts are flagged for
        manual packaging.
      </p>

      {/* Mode toggle */}
      <div className="btn-group mb-3" role="group" aria-label="Packing mode">
        <button
          type="button"
          className={`btn ${mode === 'jobs' ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => switchMode('jobs')}
        >
          <i className="fas fa-list-check me-1"></i> By Job
        </button>
        <button
          type="button"
          className={`btn ${mode === 'sales_orders' ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => switchMode('sales_orders')}
        >
          <i className="fas fa-file-invoice me-1"></i> By Sales Order
        </button>
      </div>

      {/* Filters + controls */}
      <div className="card p-3 mb-3">
        <div className="row g-3 align-items-end">
          <div className="col-md-4">
            <label className="form-label mb-1">Customer filter</label>
            <select
              className="form-select"
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
            >
              <option value="">All customers</option>
              {customers.map((c) => (
                <option key={c.customer_id} value={c.customer_id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label mb-1">
              Search {mode === 'jobs' ? 'jobs' : 'sales orders'}
            </label>
            <input
              type="text"
              className="form-control"
              placeholder={
                mode === 'jobs'
                  ? 'Job #, description, PO, customer...'
                  : 'SO #, customer PO, customer...'
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label mb-1">Max weight (lbs)</label>
            <input
              type="number"
              min="0"
              step="1"
              className="form-control"
              placeholder="No limit"
              value={maxWeight}
              onChange={(e) => setMaxWeight(e.target.value)}
            />
          </div>
          <div className="col-md-2 text-end">
            <div className="text-muted small mb-1">{selectedIds.size} selected</div>
            <button
              className="btn btn-sm btn-outline-secondary me-2"
              onClick={clearSelection}
              disabled={selectedIds.size === 0 && !plan}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      <div className="row g-3">
        {/* Picker list */}
        <div className="col-lg-5">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>
                {mode === 'jobs' ? 'Eligible jobs' : 'Eligible sales orders'}
                {loadingList ? ' (loading...)' : ` (${filteredList.length})`}
              </strong>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={selectAllFiltered}
                disabled={filteredList.length === 0}
              >
                Select all visible
              </button>
            </div>
            <div
              className="list-group list-group-flush"
              style={{ maxHeight: 600, overflowY: 'auto' }}
            >
              {loadError && (
                <div className="alert alert-danger m-2">{loadError}</div>
              )}
              {!loadingList && filteredList.length === 0 && !loadError && (
                <div className="p-3 text-muted">
                  {mode === 'jobs' ? 'No jobs match.' : 'No sales orders match.'}
                </div>
              )}
              {mode === 'jobs' &&
                filteredJobs.map((j) => (
                  <JobRow
                    key={j.job_id}
                    job={j}
                    checked={selectedJobIds.has(j.job_id)}
                    onToggle={() => toggleId(j.job_id)}
                  />
                ))}
              {mode === 'sales_orders' &&
                filteredSalesOrders.map((s) => (
                  <SalesOrderRow
                    key={s.sales_order_id}
                    so={s}
                    checked={selectedSoIds.has(s.sales_order_id)}
                    onToggle={() => toggleId(s.sales_order_id)}
                  />
                ))}
            </div>
          </div>
        </div>

        {/* Plan panel */}
        <div className="col-lg-7">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>Packing plan</strong>
              <div>
                <button
                  className="btn btn-sm btn-primary me-2"
                  onClick={handleGeneratePlan}
                  disabled={planning || selectedIds.size === 0}
                >
                  {planning ? 'Planning...' : 'Generate plan'}
                </button>
                <button
                  className="btn btn-sm btn-success"
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf || selectedIds.size === 0}
                >
                  {downloadingPdf ? 'Building PDF...' : (
                    <><i className="fas fa-file-pdf me-1"></i>Download PDF</>
                  )}
                </button>
              </div>
            </div>
            <div className="card-body">
              {planError && (
                <div className="alert alert-danger">{planError}</div>
              )}
              {!plan && !planning && !planError && (
                <div className="text-muted">
                  Select {mode === 'jobs' ? 'jobs' : 'sales orders'} and click{' '}
                  <strong>Generate plan</strong> to preview box assignments.
                  Click <strong>Download PDF</strong> to print the packing guide
                  for the shop floor.
                </div>
              )}
              {plan && <PlanView plan={plan} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function JobRow({ job, checked, onToggle }) {
  const closed = job.status === 'Completed' || job.status === 'Shipped';
  return (
    <label className="list-group-item d-flex align-items-start" style={{ cursor: 'pointer' }}>
      <input
        type="checkbox"
        className="form-check-input me-2 mt-1"
        checked={checked}
        onChange={onToggle}
      />
      <div className="flex-grow-1">
        <div className="d-flex justify-content-between">
          <strong>Job {job.master_job_num}</strong>
          <span className={`badge ${closed ? 'bg-secondary' : 'bg-primary'}`}>
            {job.status}
          </span>
        </div>
        <div className="small text-muted">
          {job.customer_name}
          {job.customer_po ? ` · PO ${job.customer_po}` : ''}
          {job.customer_so ? ` · SO ${job.customer_so}` : ''}
        </div>
        {job.job_description && <div className="small">{job.job_description}</div>}
        <div className="small text-muted">
          {job.due_date ? `Due ${fmtDate(job.due_date)}` : ''}
          {job.due_date && job.updated_at ? ' · ' : ''}
          {job.updated_at ? `Updated ${fmtDate(job.updated_at)}` : ''}
        </div>
      </div>
    </label>
  );
}

function SalesOrderRow({ so, checked, onToggle }) {
  const shippedPct = so.total_qty_ordered > 0
    ? Math.round(100 * (so.total_qty_shipped || 0) / so.total_qty_ordered)
    : 0;
  return (
    <label className="list-group-item d-flex align-items-start" style={{ cursor: 'pointer' }}>
      <input
        type="checkbox"
        className="form-check-input me-2 mt-1"
        checked={checked}
        onChange={onToggle}
      />
      <div className="flex-grow-1">
        <div className="d-flex justify-content-between">
          <strong>{so.order_number}</strong>
          <span className={`badge ${so.status === 'Completed' ? 'bg-secondary' : 'bg-primary'}`}>
            {so.status}
          </span>
        </div>
        <div className="small text-muted">
          {so.customer_name}
          {so.customer_po ? ` · PO ${so.customer_po}` : ''}
          {so.customer_so ? ` · SO ${so.customer_so}` : ''}
        </div>
        <div className="small">
          {so.job_count} job{so.job_count === 1 ? '' : 's'} ·{' '}
          {so.line_count} line{so.line_count === 1 ? '' : 's'} ·{' '}
          <span className="text-muted">
            {so.total_qty_shipped}/{so.total_qty_ordered} shipped
            {shippedPct > 0 && ` (${shippedPct}%)`}
          </span>
        </div>
        <div className="small text-muted">
          {so.promised_date ? `Promised ${fmtDate(so.promised_date)}` : ''}
        </div>
      </div>
    </label>
  );
}

function PlanView({ plan }) {
  const sos = plan.sales_orders || [];
  const jobs = plan.jobs || [];

  if (!jobs.length && !sos.length) {
    return <div className="text-muted">No content in plan.</div>;
  }

  // If we have SO roll-ups, group jobs by SO. Jobs not belonging to any
  // requested SO render in an "Other jobs" section.
  const jobsByJobId = new Map(jobs.map((j) => [j.job_id, j]));

  return (
    <div>
      {sos.map((so) => (
        <SalesOrderSummary
          key={so.sales_order_id}
          so={so}
          jobsByJobId={jobsByJobId}
        />
      ))}
      {!sos.length &&
        jobs.map((jp) => <JobPlanCard key={jp.job_id} jp={jp} />)}
    </div>
  );
}

function SalesOrderSummary({ so, jobsByJobId }) {
  const jobPlans = (so.job_ids || [])
    .map((jid) => jobsByJobId.get(jid))
    .filter(Boolean);

  return (
    <div className="border border-primary rounded p-3 mb-3" style={{ background: '#f6faff' }}>
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div>
          <h5 className="mb-0">
            <i className="fas fa-file-invoice me-1"></i>
            {so.order_number}
          </h5>
          <div className="text-muted small">
            {so.customer_name}
            {so.customer_po ? ` · PO ${so.customer_po}` : ''}
            {so.customer_so ? ` · SO ${so.customer_so}` : ''}
            {so.promised_date ? ` · Promised ${fmtDate(so.promised_date)}` : ''}
          </div>
        </div>
        <div className="text-end">
          <span className="badge bg-primary fs-6 me-1">
            {so.total_boxes} {so.total_boxes === 1 ? 'box' : 'boxes'}
          </span>
          <span className="badge bg-secondary fs-6">
            {so.n_jobs} jobs
          </span>
        </div>
      </div>

      {/* Pallet plan */}
      {so.pallet_breakdown && so.pallet_breakdown.length > 0 && (
        <div className="mb-3">
          <div className="fw-bold mb-1">Pallet plan (40 × 48, single layer)</div>
          <div className="row g-2">
            {so.pallet_breakdown.map((pb) => (
              <div key={pb.sku} className="col-md-6">
                <div className="p-2 rounded border bg-white small">
                  <strong>{pb.sku}</strong>
                  {pb.box_name && <span className="text-muted"> — {pb.box_name}</span>}
                  <div>
                    {pb.n_boxes} box{pb.n_boxes === 1 ? '' : 'es'} ·{' '}
                    {pb.per_pallet_layer}/pallet ·{' '}
                    ~{pb.est_pallets ?? '?'} pallet
                    {pb.est_pallets === 1 ? '' : 's'} ·{' '}
                    {pb.pallet_fit_clean ? 'clean fit' : 'overhang'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Line items */}
      {so.line_items && so.line_items.length > 0 && (
        <div className="mb-3">
          <div className="fw-bold mb-1">Line items (Ordered → Shipped)</div>
          <div className="table-responsive">
            <table className="table table-sm mb-0" style={{ background: 'white' }}>
              <thead className="table-light">
                <tr>
                  <th>Part</th>
                  <th className="text-end">Ordered</th>
                  <th className="text-end">Shipped</th>
                  <th>Process</th>
                </tr>
              </thead>
              <tbody>
                {so.line_items.map((li) => {
                  const proc = (li.process_steps || '').trim();
                  const laserOnly = proc.toLowerCase() === 'laser';
                  const fullyShipped = li.qty_shipped >= li.quantity_ordered && li.quantity_ordered > 0;
                  return (
                    <tr key={li.so_line_id}>
                      <td>
                        <strong>{li.product_id}</strong>
                        {li.description && li.description !== li.product_id && (
                          <span className="text-muted"> — {li.description}</span>
                        )}
                      </td>
                      <td className="text-end">{li.quantity_ordered}</td>
                      <td className="text-end">
                        {li.qty_shipped}
                        {fullyShipped && (
                          <span className="badge bg-success ms-1">✓</span>
                        )}
                      </td>
                      <td>
                        {laserOnly ? (
                          <span className="badge bg-info">Laser</span>
                        ) : (
                          <span className="badge bg-warning text-dark" title="Manual packaging required">
                            {proc || 'unknown'}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Jobs within the SO */}
      <div>
        <div className="fw-bold mb-1">Jobs ({jobPlans.length})</div>
        {jobPlans.map((jp) => (
          <JobPlanCard key={jp.job_id} jp={jp} embedded />
        ))}
      </div>
    </div>
  );
}

function JobPlanCard({ jp, embedded = false }) {
  const chosen = jp.chosen_box;
  const boxes = jp.boxes_used || [];
  const palletDirect = jp.pallet_direct || [];
  const oversized = jp.oversized || [];
  const nonLaser = jp.non_laser || [];

  const classes = embedded
    ? 'border rounded p-3 mb-2'
    : 'border rounded p-3 mb-3';

  return (
    <div className={classes} style={{ background: 'white' }}>
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div>
          <strong style={{ fontSize: '1.05em' }}>Job {jp.master_job_num}</strong>
          <div className="text-muted small">
            {jp.customer_name}
            {jp.customer_po ? ` · PO ${jp.customer_po}` : ''}
            {jp.customer_so ? ` · SO ${jp.customer_so}` : ''}
          </div>
        </div>
        <div className="text-end">
          {chosen && (
            <>
              <span className="badge bg-primary fs-6 me-1">{chosen.sku}</span>
              <span className="badge bg-secondary fs-6">
                {jp.n_boxes} {jp.n_boxes === 1 ? 'box' : 'boxes'}
              </span>
            </>
          )}
          {palletDirect.length > 0 && (
            <span className="badge bg-info fs-6 ms-1">
              {palletDirect.length} pallet-direct
            </span>
          )}
        </div>
      </div>

      {chosen ? (
        <>
          <div className="row small mb-2">
            <div className="col-md-6">
              <strong>Box:</strong> {chosen.name}
              {' · '}
              interior {chosen.interior_l}″ × {chosen.interior_w}″ ×{' '}
              {chosen.interior_h}″
            </div>
            <div className="col-md-6">
              <strong>Packable:</strong> {jp.packable_part_count} SKUs,{' '}
              {jp.packable_total_qty} pieces
            </div>
          </div>

          <div className="row g-2">
            {boxes.map((bx, idx) => (
              <div key={idx} className="col-md-6 col-lg-4">
                <BoxDiagram
                  box={chosen}
                  packed={bx}
                  label={`Box ${idx + 1} of ${boxes.length}`}
                />
              </div>
            ))}
          </div>
        </>
      ) : palletDirect.length === 0 ? (
        <div className="alert alert-warning mb-0 py-2">
          No packable parts (no flat laser-only parts fit a uniform box).
        </div>
      ) : null}

      {palletDirect.length > 0 && (
        <div className="mt-3">
          <div className="fw-bold mb-1" style={{ color: '#0d6efd' }}>
            <i className="fas fa-pallet me-1"></i>
            Pallet direct — stack on pallet, no box needed
          </div>
          <ul className="small mb-0">
            {palletDirect.map((it, i) => (
              <li key={i}>
                <strong>{it.product_id}</strong> — qty {it.quantity} —{' '}
                {it.length}″ × {it.width}″
                {it.weight_lbs > 0 && (
                  <span className="text-muted">
                    {' '}— {(it.weight_lbs * it.quantity).toFixed(1)} lbs total
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {oversized.length > 0 && (
        <div className="mt-3">
          <div className="fw-bold text-danger mb-1">
            <i className="fas fa-exclamation-triangle me-1"></i>
            Oversized — manual packaging required
          </div>
          <ul className="small mb-0">
            {oversized.map((it, i) => (
              <li key={i}>
                <strong>{it.product_id}</strong> — qty {it.quantity} —{' '}
                {it.length}″ × {it.width}″ — <em>{it.reason}</em>
              </li>
            ))}
          </ul>
        </div>
      )}

      {nonLaser.length > 0 && (
        <div className="mt-3">
          <div className="fw-bold text-warning mb-1">
            <i className="fas fa-industry me-1"></i>
            Non-laser parts — manual packaging
          </div>
          <ul className="small mb-0">
            {nonLaser.map((it, i) => (
              <li key={i}>
                <strong>{it.product_id}</strong> — qty {it.quantity} — process:{' '}
                {it.process_steps || 'unknown'}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function BoxDiagram({ box, packed, label }) {
  const boxL = Math.max(box.interior_l, box.interior_w);
  const boxW = Math.min(box.interior_l, box.interior_w);
  const svgW = 220;
  const scale = svgW / boxL;
  const svgH = boxW * scale;

  const roll = {};
  for (const pl of packed.placements || []) {
    if (!roll[pl.product_id]) {
      roll[pl.product_id] = {
        product_id: pl.product_id,
        description: pl.description,
        count: 0,
        stacks: 0,
      };
    }
    roll[pl.product_id].count += pl.count;
    roll[pl.product_id].stacks += 1;
  }

  return (
    <div className="border rounded p-2" style={{ background: '#fafafa' }}>
      <div className="small fw-bold mb-1">{label}</div>
      <svg
        width={svgW}
        height={svgH}
        style={{ display: 'block', background: 'white', border: '1px solid #222' }}
      >
        {(packed.placements || []).map((pl, i) => {
          const color = COLOR_PALETTE[(pl.color_idx ?? 0) % COLOR_PALETTE.length];
          const rx = pl.x * scale;
          const ry = (boxW - (pl.y + pl.h)) * scale;
          const rw = pl.w * scale;
          const rh = pl.h * scale;
          const fontSize = Math.max(8, Math.min(rw, rh) / 3);
          return (
            <g key={i}>
              <rect
                x={rx}
                y={ry}
                width={rw}
                height={rh}
                fill={color}
                stroke="#222"
                strokeWidth={0.5}
              />
              <text
                x={rx + rw / 2}
                y={ry + rh / 2 + fontSize * 0.35}
                textAnchor="middle"
                fontSize={fontSize}
                fontWeight="bold"
                fill="#000"
              >
                {pl.count}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="small mt-1">
        {Object.values(roll).map((r) => {
          const label = !r.description || r.description === r.product_id
            ? r.product_id
            : `${r.product_id} (${(r.description || '').slice(0, 28)})`;
          return (
            <div key={r.product_id}>
              <strong>{label}</strong> — qty {r.count}
              {r.stacks > 1 && ` (${r.stacks} stacks)`}
            </div>
          );
        })}
        {packed.total_weight > 0 && (
          <div className="text-muted">
            ~{packed.total_weight.toFixed(1)} lbs
          </div>
        )}
      </div>
    </div>
  );
}
